import { getMarketingSession } from './api/marketingApi.js';
import { getInitialAuthLabel } from './utils/marketingAuthPresentation.js';

export function homepageCtaId(link, appUrl) {
    if (link.dataset.analyticsCta) return link.dataset.analyticsCta;
    const destination = new URL(link.href);
    if (destination.origin === new URL(appUrl).origin) return 'homepage_open_althea';
    return null;
}

export function initHomepageNavigation() {
    const button = document.querySelector('.top-nav-hamburger');
    const menu = document.querySelector('.top-nav-menu');
    const close = menu.querySelector('.top-nav-menu-close');
    const background = [...document.body.children].filter((element) =>
        !element.classList.contains('top-nav') && !['SCRIPT', 'STYLE'].includes(element.tagName)
    );
    const inertState = new Map();
    function setOpen(open, restoreFocus = false) {
        menu.hidden = !open;
        button.setAttribute('aria-expanded', String(open));
        document.documentElement.classList.toggle('nav-menu-open', open);
        for (const element of background) {
            if (open) {
                inertState.set(element, element.inert);
                element.inert = true;
            } else if (inertState.has(element)) {
                element.inert = inertState.get(element);
                inertState.delete(element);
            }
        }
        if (open) close.focus();
        else if (restoreFocus) button.focus();
    }
    button.addEventListener('click', () => setOpen(menu.hidden));
    close.addEventListener('click', () => setOpen(false, true));
    document.addEventListener('keydown', (event) => {
        if (menu.hidden) return;
        if (event.key === 'Escape') setOpen(false, true);
        if (event.key === 'Tab') {
            const controls = [...menu.querySelectorAll('a[href], button')];
            const index = controls.indexOf(document.activeElement);
            const next = (index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
            event.preventDefault();
            controls[next].focus();
        }
    });
    menu.addEventListener('click', (event) => {
        if (event.target.closest('a')) setOpen(false);
    });
    matchMedia('(min-width: 1000px)').addEventListener('change', (event) => {
        if (event.matches && !menu.hidden) setOpen(false);
    });
    setOpen(false);
}

export function initDemoKeyboard() {
    document.querySelectorAll('.show-tabs[role="tablist"]').forEach((list) => {
        const tabs = [...list.querySelectorAll('.show-tab[data-panel]')];
        function update() {
            tabs.forEach((tab) => {
                tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
            });
        }
        tabs.forEach((tab) => {
            tab.id = `tab-${tab.dataset.panel}`;
            tab.setAttribute('aria-controls', tab.dataset.panel);
            document.getElementById(tab.dataset.panel)?.setAttribute('aria-labelledby', tab.id);
        });
        list.addEventListener('click', update);
        list.addEventListener('keydown', (event) => {
            const current = tabs.indexOf(event.target);
            if (current < 0) return;
            let next;
            if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
            else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = tabs.length - 1;
            else return;
            event.preventDefault();
            tabs[next].click();
            tabs[next].focus({ preventScroll: true });
        });
        update();
    });
}

export async function refreshCorpusStats() {
    if (!document.querySelector('[data-corpus-stat]')) return;
    try {
        const response = await fetch('/api/lacuna/corpus-stats', { signal: AbortSignal.timeout(5000) });
        if (!response.ok) return;
        const stats = await response.json();
        document.querySelectorAll('[data-corpus-stat]').forEach((element) => {
            const count = stats[element.dataset.corpusStat];
            if (Number.isSafeInteger(count) && count > 0) element.textContent = count.toLocaleString('en-US');
        });
        // A page that dates its counts (About) shows the date only once they are live.
        const today = new Date();
        document.querySelectorAll('[data-corpus-asof]').forEach((element) => {
            const time = element.querySelector('time');
            time.dateTime = today.toISOString().slice(0, 10);
            time.textContent = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
            element.hidden = false;
        });
    } catch {
        // Keep the rounded snapshot when Lacuna is temporarily unavailable.
    }
}


export async function refreshHomepageAuth() {
    try {
        const session = await getMarketingSession({ signal: AbortSignal.timeout(4000) });
        const authenticated = Boolean(session?.authenticated);
        window.API_CONFIG.PUBLIC_AUTHENTICATED = authenticated;
        const destination = new URL(authenticated ? '/' : '/login', window.API_CONFIG.APP_URL).href;
        document.querySelectorAll('.top-nav-ecosystem-link, .top-nav-menu-cta').forEach((link) => {
            const mobile = link.classList.contains('top-nav-menu-cta');
            link.textContent = getInitialAuthLabel();
            link.href = destination;
            link.dataset.analyticsCta = authenticated
                ? `homepage_account_${mobile ? 'mobile' : 'desktop'}`
                : `header_auth_${mobile ? 'mobile' : 'desktop'}`;
        });
        document.querySelector('.top-nav-auth-slot')?.setAttribute(
            'data-auth-state', authenticated ? 'signed-in' : 'signed-out'
        );
    } catch {
        // Keep the last confirmed presentation if the session endpoint is unavailable.
    }
}
